import Locker from "@components/global/Locker/Locker";
import Miner from "@components/global/new/Miner/Miner";
import { RACK_ITEM_DRAG_END_EVENT } from "@mobx/components/RackTable3/dropOutside";
import { ItemStore } from "@mobx/store/locations/interfaces";
import { DRAGGING_TYPES3, IDragRackItemData3, IRackItem3 } from "@mobx/store/locations/types";
import { useScrollByDragging } from "@utils/hooks/useScrollByDragging";
import cn from "classnames";
import { observer } from "mobx-react-lite";
import { DragEvent, useEffect, useMemo, useRef, useState } from "react";
import Merge3 from "../Merge3";
import TooltipMenu3 from "../TooltipMenu3";
import styles from "./Front.module.scss";

type DragEventType = DragEvent<HTMLDivElement>;
export interface IFront3Props {
  itemStore: ItemStore;
  disableNativeDrag?: boolean;
}

const useControl = (props: IFront3Props) => {
  const dragCounter = useRef<number>(0);
  const rackStore = props.itemStore.rackStore;
  const rack = rackStore.rack;
  const {
    item,
    isLoading,
    isLocked,
    isMerging,
    itemIndex,
    setLocked,
    setMerging,
    setMeta,
  } = props.itemStore;
  const rackItem = item as IRackItem3;
  const isDragRef = useRef(false);
  const [isDrag, setIsDrag] = useState<boolean>(false);
  const [dragOver, setDragOver] = useState<boolean>(false);
  useScrollByDragging(isDrag);

  const dispatchRackItemDragEnd = (point: { x: number; y: number }) => {
    window.dispatchEvent(
      new CustomEvent(RACK_ITEM_DRAG_END_EVENT, {
        detail: {
          item: getRackItemDragData(),
          point,
        },
      })
    );
  };

  const installNativeDragEndHandlers = () => {
    const cleanup = () => {
      window.removeEventListener("dragend", onWindowDragEnd, true);
      window.removeEventListener("dragover", onWindowDragOver, true);
      window.removeEventListener("drop", onWindowDrop, true);
      document.removeEventListener("dragend", onDocumentDragEnd, true);
      document.removeEventListener("dragover", onDocumentDragOver, true);
      document.removeEventListener("drop", onDocumentDrop, true);
    };

    const handleNativeEnd = (ev: globalThis.DragEvent) => {
      const point = { x: ev.clientX, y: ev.clientY };
      cleanup();
      setTimeout(() => dispatchRackItemDragEnd(point), 0);
    };

    function onWindowDragEnd(ev: globalThis.DragEvent) {
      handleNativeEnd(ev);
    }
    function onWindowDragOver(ev: globalThis.DragEvent) {
      ev.preventDefault();
    }
    function onWindowDrop(ev: globalThis.DragEvent) {
      handleNativeEnd(ev);
    }
    function onDocumentDragEnd(ev: globalThis.DragEvent) {
      handleNativeEnd(ev);
    }
    function onDocumentDragOver(ev: globalThis.DragEvent) {
      ev.preventDefault();
    }
    function onDocumentDrop(ev: globalThis.DragEvent) {
      handleNativeEnd(ev);
    }

    window.addEventListener("dragend", onWindowDragEnd, true);
    window.addEventListener("dragover", onWindowDragOver, true);
    window.addEventListener("drop", onWindowDrop, true);
    document.addEventListener("dragend", onDocumentDragEnd, true);
    document.addEventListener("dragover", onDocumentDragOver, true);
    document.addEventListener("drop", onDocumentDrop, true);
  };

  const getRackItemDragData = (): IDragRackItemData3 => ({
    ...rackItem,
    prevRackId: `${rack.id}`,
    prevIndex: itemIndex,
  });

  const onDragStart = (e: DragEventType) => {
    const rackItemData = getRackItemDragData();
    const stringData = JSON.stringify(rackItemData);
    isDragRef.current = true;
    setIsDrag(true);
    installNativeDragEndHandlers();

    window.TRANSFER_RACK_ITEM = {
      prevIndex: itemIndex,
      draggingItem: { ...rackItem, user_id: "" + rackItem.user_id },
      prevRackItems: [],
    };

    e.dataTransfer.setData(DRAGGING_TYPES3.RACK_ITEM, stringData);
    e.dataTransfer.dropEffect = "move";
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragEnter = (e: DragEventType) => {
    e.preventDefault();
    setDragOver(true);
    dragCounter.current++;
  };

  const onDrop = (e: DragEventType) => {
    isDragRef.current = false;
    setIsDrag(false);
    setDragOver(false);
    window.TRANSFER_RACK_ITEM = undefined;
  };

  const onDragEnd = (e: DragEventType) => {
    if (!isDragRef.current) return;
    isDragRef.current = false;

    dispatchRackItemDragEnd({ x: e.clientX, y: e.clientY });

    setIsDrag(false);
    setDragOver(false);
    window.TRANSFER_RACK_ITEM = undefined;
  };

  const onDragLeave = (e: DragEventType) => {
    dragCounter.current--;
    e.preventDefault();
    e.stopPropagation();
    if (dragCounter.current === 0) {
      setDragOver(false);
    }
  };

  const onLockHandler = () => {
    //dbg.log("LOCKED", isLocked);
    setLocked(!isLocked);
  };

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMerging(false);
      }
    };
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, []);

  const contextMenuPlacement = useMemo(() => {
    return rackItem.x > 1 && rackItem.x === rack.rack_type[0]
      ? "leftBottom" as const
      : "rightBottom" as const;
  }, [rackItem.x, rack.rack_type[0]]);

  const copyPopoverPlacement = useMemo(() => {
    return rackItem.x > 1 && rackItem.x === rack.rack_type[0]
      ? "left" as const
      : "right" as const;
  }, [rackItem.x, rack.rack_type[0]]);

  return {
    isDrag,
    dragOver,
    merging: isMerging,
    locked: isLocked,
    showLoader: isLoading,
    item: rackItem,
    contextMenuPlacement,
    copyPopoverPlacement,
    onDragStart,
    onDragEnter,
    onDragEnd,
    onDrop,
    onDragLeave,
    onLockHandler,
    updateFlagRackItem: setMeta,
  };
};

const Front3 = observer((props: IFront3Props) => {
  const control = useControl(props);

  return (
    <TooltipMenu3
      itemStore={props.itemStore}
      placement={control.contextMenuPlacement}
    >
      <div
        className={cn(styles.wrapper, {
          [styles.canDrag]: !control.locked,
          [styles.draging]: control.isDrag,
          [styles.dragOver]: control.dragOver,
          [styles.mergeStarted]: control.merging,
        })}
        draggable={!props.disableNativeDrag && !control.merging && !control.locked}
        onDragStart={control.onDragStart}
        onDragEnter={control.onDragEnter}
        onDrop={control.onDrop}
        onDragEnd={control.onDragEnd}
        onDragLeave={control.onDragLeave}
        onDragOver={(e) => e.preventDefault()}
      >
        {control.dragOver && <div className={styles.restricted} />}

        <Miner
          miner_id={control.item.miner_id}
          miner_name={control.item.miner_name}
          rejected={control.item.rejected}
          accepted={control.item.accepted}
          user_color={control.item.user_color}
          user_id={"" + control.item.user_id}
          reproxy_link={control.item.reproxy_link}
          status={control.item.status}
          loader={control.showLoader}
          bottomClassName={cn({
            [styles.unmerge]: control.item.merge?.length,
          })}
          setOptimize={v => props.itemStore.rackStore.setReduced(v)}
          meta={control.item.meta}
          updateFlagRackItem={control.updateFlagRackItem}
          speed={control.item.speed}
          copyPopoverPlacement={control.copyPopoverPlacement}
        >
          {!control.merging && (
            <Locker
              className={styles.lock}
              lock={control.locked}
              color={control.item.user_color}
              onClick={control.onLockHandler}
            />
          )}
          <Merge3 itemStore={props.itemStore} color={control.item.user_color} />
        </Miner>
      </div>
    </TooltipMenu3>
  );
});

Front3.displayName = "Front3";

export default Front3;
